"use client";

import React from "react";
import { Control, Controller } from "react-hook-form";
import {
  Box, FormControl,
  FormControlLabel, FormHelperText,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography, useMediaQuery
} from "@mui/material";
import { useLocale, useTranslations } from "next-intl";
import isEmail from "@/utils/validation/is-email-util";
import { validatePhoneByLocale } from "@/utils/validation/verify-phone-by-locale";
import { ColletteCheckbox, ColletteButton } from "@collette-travel/collette-common-controls";
import { Locale } from "@/i18n/routing";
import ManagedReCAPTCHA from "@/components/managed-recaptcha/managed-recaptcha";
import {NeedMoreInfoFormData} from "@/types/leads/lead-forms";

interface NeedMoreInfoFormProps {
  control: Control<NeedMoreInfoFormData, any>;
  handleSubmit: React.FormEventHandler<HTMLFormElement> | undefined;
  isInvalid: boolean;
  isSubmitting: boolean;
}

const NeedMoreInfoForm = ({ control, handleSubmit, isInvalid, isSubmitting }: NeedMoreInfoFormProps) => {
  const t = useTranslations("FORMS");
  const locale = useLocale() as Locale;
  const shrinkReCaptcha = useMediaQuery("(max-width:425px)");

  return (
    <Box
      component="form"
      noValidate
      onSubmit={handleSubmit}
      data-testid="need-more-info-form"
      data-form-name="Need More Info"
      data-form-id="need-more-info-form"
      data-track="visibility"
    >
      <Stack
        sx={{
          flexDirection: "column",
          gap: "1.6rem"
        }}
      >
        <Typography component="p" variant="h6" data-testid="need-more-info-contact-section-header">
          {t("Contact Information")}
        </Typography>
        <Stack
          component="fieldset"
          sx={{
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: "1.6rem", md: "3.2rem" },
            border: "none",
            padding: 0
          }}
        >
          <Controller
            name="firstName"
            control={control}
            rules={{
              required: t("Please complete this required field")
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                required
                data-testid="need-more-info-first-name-field"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                aria-label={t("First Name")}
                placeholder={t("First Name")}
                label={t("First Name")}
                sx={{
                  width: { xs: "100%", md: "50%" }
                }}
              />
            )}
          />
          <Controller
            name="lastName"
            control={control}
            rules={{
              required: t("Please complete this required field")
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                required
                data-testid="need-more-info-last-name-field"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                aria-label={t("Last Name")}
                placeholder={t("Last Name")}
                label={t("Last Name")}
                sx={{
                  width: { xs: "100%", md: "50%" }
                }}
              />
            )}
          />
        </Stack>
        <Stack
          component="fieldset"
          sx={{
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: "1.6rem", md: "3.2rem" },
            border: "none",
            padding: 0
          }}
        >
          <Controller
            name="email"
            control={control}
            rules={{
              required: t("Please complete this required field"),
              validate: (value) => isEmail(value) || t("Email must be formatted correctly")
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                required
                data-testid="need-more-info-email-field"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                aria-label={t("Email")}
                placeholder={t("Email")}
                label={t("Email")}
                sx={{
                  width: { xs: "100%", md: "50%" }
                }}
              />
            )}
          />
          <Controller
            name="phone"
            control={control}
            rules={{
              required: t("Please complete this required field"),
              validate: (value) => validatePhoneByLocale(value, locale, t)
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                required
                data-testid="need-more-info-phone-field"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                aria-label={t("Phone")}
                placeholder={t("Phone")}
                label={t("Phone")}
                sx={{
                  width: { xs: "100%", md: "50%" }
                }}
              />
            )}
          />
        </Stack>
        <Controller
          name="isTravelAgent"
          control={control}
          rules={{
            required: t("Please complete this required field")
          }}
          render={({ field, fieldState }) => (
            <FormControl error={!!fieldState.error}>
              <Stack
                sx={{
                  gap: "1.2rem",
                  alignItems: {xs: "flex-start", md: "center"},
                  flexDirection: {xs: "column", md: "row"}
                }}
              >
                <Typography
                  sx={{
                    "&::after": { content: "\"*\"", ariaHidden: "true" }
                  }}
                  data-testid="need-more-info-travel-advisor-label"
                >
                  {t("Are you a travel advisor?")}
                </Typography>
                <RadioGroup
                  {...field}
                  value={field.value ?? ""}
                  onChange={(_, value) => field.onChange(value)}
                  row
                  aria-label={t("Are you a travel advisor?")}
                  data-testid="need-more-info-travel-advisor-radio-group"
                  sx={{
                    gap: "3.2rem"
                  }}
                >
                  <FormControlLabel
                    sx={{
                      margin: 0
                    }}
                    control={
                      <Radio
                        sx={{
                          "& .MuiSvgIcon-root": {
                            color: "primary.main",
                            fill: "primary.main"
                          }
                        }}
                      />
                    }
                    label={t("Yes")}
                    value="yes"
                  />
                  <FormControlLabel
                    sx={{
                      margin: 0
                    }}
                    control={
                      <Radio
                        sx={{
                          "& .MuiSvgIcon-root": {
                            color: "primary.main",
                            fill: "primary.main"
                          }
                        }}
                      />
                    }
                    label={t("No")}
                    value="no"
                  />
                </RadioGroup>
              </Stack>
              <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
          )}
        />
        <Stack gap={1}>
          <Controller
            name="optInEmail"
            control={control}
            render={({ field }) => (
              <ColletteCheckbox
                {...field}
                checked={!!field.value}
                testId="need-more-info-opt-in-email-checkbox-field"
                label={t("Email opt in")}
                onChange={(e) => field.onChange(e.target.checked)}
              />
            )}
          />
          <Controller
            name="workingWithAgent"
            control={control}
            render={({ field }) => (
              <ColletteCheckbox
                {...field}
                checked={!!field.value}
                testId="need-more-info-working-with-agent-checkbox-field"
                label={t("Working with travel professional")}
                onChange={(e) => field.onChange(e.target.checked)}
              />
            )}
          />
          <Controller
            name="withMoreTravelers"
            control={control}
            render={({ field }) => (
              <ColletteCheckbox
                {...field}
                checked={!!field.value}
                testId="need-more-info-with-more-travelers-checkbox-field"
                label={t("Traveling with 8 or more people")}
                onChange={(e) => field.onChange(e.target.checked)}
              />
            )}
          />
        </Stack>
        <Controller
          name="recaptchaToken"
          control={control}
          rules={{
            required: t("Please complete this required field")
          }}
          render={({ field, fieldState }) => (
            <FormControl error={!!fieldState.error}>
              <Box
                sx={{
                  transformOrigin: "0 0",
                  transform: `scale(${shrinkReCaptcha ? "0.7" : "1" })`
                }}
              >
                <ManagedReCAPTCHA
                  size="normal"
                  sitekey="6LfV8zIpAAAAALFDQIb2-f4xUO3PrI8vVhrgwA9x"
                  onChange={(token) => field.onChange(token)}
                />
              </Box>
              <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
          )}
          data-testid="need-more-info-captcha"
        />

        {/* VALIDATION MESSAGE */}
        {isInvalid && (
          <Box
            sx={{
              padding: "0.4rem 1rem",
              backgroundColor: "error.100",
              borderRadius: "1rem",
              color: "error.dark"
            }}
          >
            <Typography>
              {t("Please complete all required fields")}
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: { xs: "center", md: "end" }
          }}
        >
          <ColletteButton
            disabled={isSubmitting}
            label={t("Request Information")}
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            testId="need-more-info-submit-button"
          />
        </Box>
      </Stack>
    </Box>
  );
};

export default NeedMoreInfoForm;
